import { authService } from '@/services/auth.service';
import {
  API_BASE_URL,
  API_PROXY_DISABLE_REWRITE,
  API_PROXY_PREFIX,
  API_TARGET_URL,
  USING_DEV_PROXY,
} from '@/lib/data-source/base-url';
import { getProviderDomain, getProviderSlug } from '@/lib/provider-context';

type RequestMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export class ProviderSettingsRequestError extends Error {
  status?: number;
  payload?: unknown;

  constructor(message: string, status?: number, payload?: unknown) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

const AUTH_SETTINGS_PAT_ENV =
  typeof import.meta.env.VITE_PROVIDER_AUTH_SETTINGS_TOKEN === 'string'
    ? import.meta.env.VITE_PROVIDER_AUTH_SETTINGS_TOKEN.trim()
    : '';

const AUTH_SETTINGS_PAT_STORAGE_KEY = 'florentin.provider.auth_settings_token';

const PROVIDER_SLUG_HEADER_OVERRIDE =
  typeof import.meta.env.VITE_PROVIDER_SLUG_HEADER === 'string'
    ? import.meta.env.VITE_PROVIDER_SLUG_HEADER
    : undefined;

const PROVIDER_DOMAIN_HEADER_OVERRIDE =
  typeof import.meta.env.VITE_PROVIDER_DOMAIN_HEADER === 'string'
    ? import.meta.env.VITE_PROVIDER_DOMAIN_HEADER
    : undefined;

const DEV_PROVIDER_SLUG =
  typeof import.meta.env.VITE_DEVELOPMENT_PROVIDER_SLUG === 'string'
    ? import.meta.env.VITE_DEVELOPMENT_PROVIDER_SLUG
    : undefined;

function getOverrideToken(): string | null {
  if (AUTH_SETTINGS_PAT_ENV.length > 0) {
    return AUTH_SETTINGS_PAT_ENV;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  const stored = window.localStorage.getItem(AUTH_SETTINGS_PAT_STORAGE_KEY);
  return stored && stored.trim().length > 0 ? stored.trim() : null;
}

function resolveRequestPath(path: string): string {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  if (USING_DEV_PROXY && API_PROXY_PREFIX && !API_PROXY_DISABLE_REWRITE) {
    if (normalizedPath.startsWith(API_PROXY_PREFIX)) {
      return normalizedPath;
    }

    const prefixed = `${API_PROXY_PREFIX}${normalizedPath}`;
    return prefixed.replace(`${API_PROXY_PREFIX}//`, `${API_PROXY_PREFIX}/`);
  }

  const base = API_BASE_URL || API_TARGET_URL || '';
  if (!base || base === '/') {
    return normalizedPath;
  }

  return `${base}${normalizedPath}`;
}

function resolveProviderSlug(): string | null {
  const stored = getProviderSlug();
  if (stored) {
    return stored;
  }

  if (PROVIDER_SLUG_HEADER_OVERRIDE) {
    return PROVIDER_SLUG_HEADER_OVERRIDE;
  }

  if (import.meta.env.DEV && DEV_PROVIDER_SLUG) {
    return DEV_PROVIDER_SLUG;
  }

  return null;
}

function resolveProviderDomain(): string | null {
  const stored = getProviderDomain();
  if (stored) {
    return stored;
  }

  if (PROVIDER_DOMAIN_HEADER_OVERRIDE) {
    return PROVIDER_DOMAIN_HEADER_OVERRIDE;
  }

  if (typeof window !== 'undefined') {
    return window.location.hostname;
  }

  return null;
}

interface RequestOptions {
  method?: RequestMethod;
  body?: Record<string, unknown> | undefined;
}

export async function requestProviderSettings(
  path: string,
  options: RequestOptions = {},
): Promise<Record<string, unknown>> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Florentin-Auth-Scope': 'provider',
  };

  if (method !== 'GET' && method !== 'DELETE') {
    headers['Content-Type'] = 'application/json';
  }

  const overrideToken = getOverrideToken();
  const sessionToken = authService.getAuthToken('provider');
  const authToken = overrideToken ?? sessionToken;
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  const providerSlug = resolveProviderSlug();
  const providerDomain = resolveProviderDomain();
  if (providerSlug) {
    headers['X-Provider-Slug'] = providerSlug;
  }
  if (providerDomain) {
    headers['X-Provider-Domain'] = providerDomain;
  }

  const response = await fetch(resolveRequestPath(path), {
    method,
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  let payload: Record<string, unknown> | null = null;
  try {
    payload = (await response.json()) as Record<string, unknown>;
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const message = (payload as { message?: string } | null)?.message ?? 'Request failed';
    throw new ProviderSettingsRequestError(message, response.status, payload ?? undefined);
  }

  return payload ?? {};
}
